import type { Draft } from "immer";
import type { StoreApi } from "zustand";

import type {
  Attribute,
  AttributeCollection,
  AttributeCollectionId,
  AttributeGroup,
  AttributeGroupId,
} from "../../../types";
import { createFieldId, parseFieldId } from "../../../utils/field-id";
import { getDynamicGroupInstances } from "../../selectors/field-selectors";
import type {
  DynamicFieldActions,
  FormStore,
  SliceSetFunction,
} from "../../types";

/**
 * 動的フィールド操作に関するアクション
 * - 動的コレクション追加/削除
 * - 動的グループ追加/削除
 * - 動的属性追加/削除
 */
export const createDynamicFieldActions = (
  set: SliceSetFunction,
  _get: () => FormStore,
  _store: StoreApi<FormStore>,
): DynamicFieldActions => ({
  /**
   * 動的コレクション追加
   */
  addDynamicCollection: (collection: AttributeCollection) => {
    set(
      (state: Draft<FormStore>) => {
        if (!state.form?.form?.attributeCollections) {
          return state;
        }

        const collectionId = collection.id;

        // 新しいコレクションの作成
        const newCollection: AttributeCollection = {
          ...collection,
          id: collectionId,
          attributeGroupSettings: collection.attributeGroupSettings || [],
          isDynamic: true,
        };

        // フォーム定義の更新
        return {
          form: {
            ...state.form,
            form: {
              ...state.form.form,
              attributeCollections: [
                ...state.form.form.attributeCollections,
                newCollection,
              ],
            },
          },
        };
      },
      false,
      "dynamic/addCollection",
    );
  },

  /**
   * 動的グループ追加
   */
  addDynamicGroup: (
    collectionId: AttributeCollectionId,
    group: AttributeGroup,
  ) => {
    set(
      (state: Draft<FormStore>) => {
        if (!state.form?.form?.attributeCollections) {
          return state;
        }

        const groupId = group.id;

        // 既存のグループ設定を取得
        const collection = state.form.form.attributeCollections.find(
          (c) => c.id === collectionId,
        );
        if (!collection) {
          return state;
        }

        const groupSetting = collection.attributeGroupSettings.find(
          (g) => g.id === groupId,
        );
        if (!groupSetting) {
          return state;
        }

        // 新しいグループのインスタンス数を取得
        const groupIndex = getDynamicGroupInstances(
          state,
          collectionId,
          groupId,
        );

        // 新しいフィールドの値を追加
        const newValues = [...state.values];
        groupSetting.attributes.forEach((attribute) => {
          const fieldId = createFieldId({
            collectionId,
            collectionIndex: 0,
            groupId,
            groupIndex,
            attributeId: attribute.id,
          });

          newValues.push({
            fieldId,
            attributeId: attribute.id,
            value: undefined,
          });
        });

        return {
          values: newValues,
        };
      },
      false,
      "dynamic/addGroup",
    );
  },

  /**
   * 動的属性追加
   */
  addDynamicAttribute: (
    collectionId: AttributeCollectionId,
    groupId: AttributeGroupId,
    attribute: Attribute,
  ) => {
    set(
      (state: Draft<FormStore>) => {
        if (!state.form?.form?.attributeCollections) {
          return state;
        }

        const collection = state.form.form.attributeCollections.find(
          (c) => c.id === collectionId,
        );

        if (!collection) {
          return state;
        }

        const group = collection.attributeGroupSettings?.find(
          (g) => g.id === groupId,
        );

        if (!group) {
          return state;
        }

        // 新しい属性の作成
        const newAttribute: Attribute = {
          ...attribute,
          id: attribute.id,
          // NOTE: 動的なfield(attribute)の要件がすぐに発生するが、初期リリースでは不要になる可能性があるためコメントアウトしている
          // isDynamic: true,
        };

        // グループ内の属性を更新
        const updatedCollections = state.form.form.attributeCollections.map(
          (c) => {
            if (c.id === collectionId) {
              return {
                ...c,
                attributeGroupSettings: c.attributeGroupSettings?.map((g) => {
                  if (g.id === groupId) {
                    return {
                      ...g,
                      attributes: [...(g.attributes || []), newAttribute],
                    };
                  }
                  return g;
                }),
              };
            }
            return c;
          },
        );

        return {
          form: {
            ...state.form,
            form: {
              ...state.form.form,
              attributeCollections: updatedCollections,
            },
          },
        };
      },
      false,
      "dynamic/addAttribute",
    );
  },

  /**
   * 動的コレクション削除
   */
  removeDynamicCollection: (
    collectionId: AttributeCollectionId,
    collectionIndex?: number,
  ) => {
    set(
      (state: Draft<FormStore>) => {
        if (!state.form?.form?.attributeCollections) {
          return state;
        }

        // 特定のインデックスが指定されている場合、そのインデックスのコレクションのみを削除
        if (collectionIndex !== undefined) {
          // 削除するコレクションを特定
          const collectionsWithId = state.form.form.attributeCollections.filter(
            (c) => c.id === collectionId,
          );

          // 該当IDのコレクションが存在しない場合は処理中止
          if (collectionsWithId.length === 0) {
            console.warn(
              `指定されたIDのコレクションが見つかりません: ${collectionId}`,
            );
            return state;
          }

          // コレクションIDと一致するコレクションのみを処理
          let updatedCollections = [...state.form.form.attributeCollections];

          // 同じIDを持つコレクションのインデックスを取得
          const targetIndices = updatedCollections
            .map((c, i) => (c.id === collectionId ? i : -1))
            .filter((i) => i !== -1);

          // インデックスが範囲外の場合は処理中止
          if (collectionIndex >= targetIndices.length) {
            console.warn(
              `指定されたインデックス(${collectionIndex})はコレクション数(${targetIndices.length})より大きいため削除できません`,
            );
            return state;
          }

          // 実際のコレクション配列内のインデックス
          const actualIndex = targetIndices[collectionIndex];

          // コレクションを削除
          updatedCollections = updatedCollections.filter(
            (_, i) => i !== actualIndex,
          );

          // フォーム定義が完全に空にならないようにする
          if (updatedCollections.length === 0) {
            // 削除対象のコレクションをテンプレートとして使用
            const targetCollection =
              state.form.form.attributeCollections[actualIndex];
            updatedCollections = [
              {
                ...targetCollection,
              },
            ];
          }

          // 削除するコレクションに関連する値をフィルタリング
          const newValues = state.values.filter((value) => {
            const parsedFieldId = parseFieldId(value.fieldId);
            // 特定のコレクションIDと特定のインデックスに一致する値を削除
            const shouldRemove =
              parsedFieldId?.collectionId === collectionId &&
              parsedFieldId?.collectionIndex === collectionIndex;
            return !shouldRemove;
          });

          // 削除したインデックス以降のフィールドIDを調整
          const adjustedValues = newValues.map((value) => {
            const parsedFieldId = parseFieldId(value.fieldId);
            if (
              parsedFieldId &&
              parsedFieldId.collectionId === collectionId &&
              parsedFieldId.collectionIndex > collectionIndex
            ) {
              // インデックスを1つ減らした新しいフィールドIDを生成
              const newFieldId = createFieldId({
                collectionId: parsedFieldId.collectionId,
                collectionIndex: parsedFieldId.collectionIndex - 1,
                groupId: parsedFieldId.groupId,
                groupIndex: parsedFieldId.groupIndex,
                attributeId: parsedFieldId.attributeId,
              });

              return {
                ...value,
                fieldId: newFieldId,
              };
            }
            return value;
          });

          return {
            form: {
              ...state.form,
              form: {
                ...state.form.form,
                attributeCollections: updatedCollections,
              },
            },
            values: adjustedValues,
          };
        }

        // ここは実行されない想定 - インデックスを常に指定するように修正
        console.warn(
          "コレクションインデックスが指定されていません - すべてのコレクションは削除されません",
        );
        return state;
      },
      false,
      "dynamic/removeCollection",
    );
  },

  /**
   * 動的グループ削除
   */
  removeDynamicGroup: (
    collectionId: AttributeCollectionId,
    groupId: AttributeGroupId,
  ) => {
    set(
      (state: Draft<FormStore>) => {
        // グループに関連するフィールドの値を削除
        const newValues = state.values.filter((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          return !(
            parsedFieldId?.collectionId === collectionId &&
            parsedFieldId?.groupId === groupId
          );
        });

        return {
          values: newValues,
        };
      },
      false,
      "dynamic/removeGroup",
    );
  },

  /**
   * 動的属性削除
   */
  removeDynamicAttribute: (
    collectionId: AttributeCollectionId,
    groupId: AttributeGroupId,
    attributeId: string,
  ) => {
    set(
      (state: Draft<FormStore>) => {
        if (!state.form?.form?.attributeCollections) {
          return state;
        }

        // 属性を削除
        const updatedCollections = state.form.form.attributeCollections.map(
          (c) => {
            if (c.id === collectionId) {
              return {
                ...c,
                attributeGroupSettings: c.attributeGroupSettings?.map((g) => {
                  if (g.id === groupId) {
                    return {
                      ...g,
                      attributes: g.attributes?.filter(
                        (a) => a.id !== attributeId,
                      ),
                    };
                  }
                  return g;
                }),
              };
            }
            return c;
          },
        );

        return {
          form: {
            ...state.form,
            form: {
              ...state.form.form,
              attributeCollections: updatedCollections,
            },
          },
        };
      },
      false,
      "dynamic/removeAttribute",
    );
  },
});
